<script lang="ts" setup>
  import { ref, type Ref, onMounted, onBeforeUnmount, onUpdated } from 'vue';
  import type { NavButtons } from '@storefront/types';
  import Swiper from 'swiper';
  import { Navigation } from 'swiper/modules';
  import 'swiper/css';
  import 'swiper/css/navigation';

  defineOptions({
    inheritAttrs: false,
  });

  const props = defineProps({
    header: { type: String },
    headerType: { type: String },
    place_details: { type: Object },
  });

  const htmlElement = ref() as Ref<HTMLElement>;
  const htmlNavigation = ref() as Ref<NavButtons>;

  const swiper = ref() as Ref<Swiper>;

  function initializeSwipers() {
    if (props.place_details) {
      swiper.value = new Swiper(htmlElement.value, {
        modules: [Navigation],
        slidesPerView: 1,
        navigation: {
          nextEl: htmlNavigation.value.next.htmlElement,
          prevEl: htmlNavigation.value.prev.htmlElement,
        },
        breakpoints: {
          576: {
            slidesPerView: 1.5,
          },
          992: {
            slidesPerView: 2,
          },
          1200: {
            slidesPerView: 3,
          },
        },
      });
    }
  }

  function destroySwipers() {
    swiper.value?.destroy();
  }

  onMounted(() => {
    initializeSwipers();
  });

  onBeforeUnmount(() => {
    destroySwipers();
  });

  onUpdated(() => {
    initializeSwipers();
  });

  onBeforeUnmount(() => {
    destroySwipers();
  });
</script>
<template>
  <!--  @TODO-->
  <SfSection class="google-review container" title="header" :headerType>
    <div v-if="place_details" class="reviews-carousel">
      <div class="reviews-carousel__left">
        <div class="reviews-carousel__average">
          {{ place_details.averageRating.toFixed(1) }}
          <SfStars :rating="place_details.averageRating"/>
        </div>
        <div>
          {{ $tc('SHOP.COMMON.MESSAGE.GOOGLE_RATING_COUNT', place_details.ratingsTotal, { ratingsTotal: place_details.ratingsTotal }) }}
        </div>
        <div class="reviews-carousel__google-logo-wrapper">
          <svg width="80" height="25" viewBox="0 0 80 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.99998 8.9041V11.5816H16.4263C16.2382 13.0759 15.7367 14.1656 14.953 14.9439C14.0125 15.8779 12.5392 16.9053 9.99998 16.9053C6.05015 16.9053 2.9467 13.7297 2.9467 9.80696C2.9467 5.88418 6.0188 2.67746 9.99998 2.67746C12.1316 2.67746 13.699 3.51805 14.8589 4.57658L16.7398 2.70859C15.141 1.18306 12.978 0 9.99998 0C4.5768 0 0 4.38978 0 9.77583C0 15.1619 4.5768 19.5517 9.99998 19.5517C12.9467 19.5517 15.141 18.5865 16.8652 16.8119C18.652 15.0373 19.1849 12.5778 19.1849 10.5542C19.1849 9.93149 19.1536 9.3711 19.0282 8.87296L9.99998 8.9041Z" fill="#4285F4" />
            <path d="M27.2407 6.72485C23.7297 6.72485 20.8457 9.37118 20.8457 13.0449C20.8457 16.6875 23.6984 19.3649 27.2407 19.3649C30.783 19.3649 33.6357 16.6875 33.6357 13.0449C33.6043 9.37118 30.7516 6.72485 27.2407 6.72485ZM27.2407 16.8743C25.3285 16.8743 23.667 15.2865 23.667 13.0449C23.667 10.7722 25.3285 9.21551 27.2407 9.21551C29.1529 9.21551 30.8143 10.7722 30.8143 13.0449C30.8143 15.2865 29.1529 16.8743 27.2407 16.8743Z" fill="#EA4335" />
            <path d="M58.4962 8.12585H58.4021C57.7752 7.37865 56.5839 6.72485 55.0479 6.72485C51.8818 6.72485 49.0918 9.46458 49.0918 13.0449C49.0918 16.5941 51.8504 19.3649 55.0479 19.3649C56.5839 19.3649 57.7752 18.68 58.4021 17.9328H58.4962V18.8045C58.4962 21.2018 57.1796 22.5094 55.1106 22.5094C53.4178 22.5094 52.352 21.2952 51.9131 20.2678L49.4993 21.2641C50.189 22.9453 52.0385 25 55.1419 25C58.4021 25 61.1921 23.1009 61.1921 18.4309V7.06732H58.5589V8.12585H58.4962ZM55.2987 16.8743C53.3865 16.8743 51.9131 15.2554 51.9131 13.0449C51.9131 10.8033 53.3865 9.21551 55.2987 9.21551C57.2109 9.21551 58.6843 10.8344 58.6843 13.076C58.7156 15.2554 57.2109 16.8743 55.2987 16.8743Z" fill="#4285F4" />
            <path d="M41.4731 6.72485C37.9621 6.72485 35.0781 9.37118 35.0781 13.0449C35.0781 16.6875 37.9308 19.3649 41.4731 19.3649C45.0154 19.3649 47.8681 16.6875 47.8681 13.0449C47.8367 9.37118 44.9841 6.72485 41.4731 6.72485ZM41.4731 16.8743C39.5609 16.8743 37.8994 15.2865 37.8994 13.0449C37.8994 10.7722 39.5609 9.21551 41.4731 9.21551C43.3853 9.21551 45.0468 10.7722 45.0468 13.0449C45.0468 15.2865 43.3853 16.8743 41.4731 16.8743Z" fill="#FBBC05" />
            <path d="M63.3535 0.249023H66.1121V19.3337H63.3535V0.249023Z" fill="#34A853" />
            <path d="M74.5762 16.8742C73.1656 16.8742 72.1624 16.2204 71.5041 14.975L79.9994 11.4881L79.7173 10.772C79.1844 9.37105 77.5856 6.75586 74.2941 6.75586C71.0339 6.75586 68.3066 9.30878 68.3066 13.0759C68.3066 16.6251 71.0026 19.3959 74.6076 19.3959C77.5229 19.3959 79.2157 17.6213 79.9054 16.594L77.7424 15.1618C76.99 16.1581 75.9869 16.8742 74.5762 16.8742ZM74.3881 9.09085C75.5167 9.09085 76.4884 9.68238 76.8019 10.4918L71.0653 12.858C71.0339 10.1805 72.9461 9.09085 74.3881 9.09085Z" fill="#EA4335" />
          </svg>
        </div>
      </div>
      <div class="items-carousel">
        <div ref="htmlElement" class="swiper reviews-carousel__container">
          <div class="swiper-wrapper">
            <div v-for="review in place_details.reviews" class="swiper-slide items-carousel__slide">
              <div class="reviews-carousel__slide">
                <div class="reviews-carousel__author-name">
                  {{ review.author_name }}
                </div>
                <SfDateTime :value="review.created_at" />
                <SfStars :rating="review.rating" />
                <p class="reviews-carousel__text">
                  {{ review.text }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <SfCarouselButtons ref="htmlNavigation" />
      </div>
    </div>
    <SfDataPlaceholder v-else>
      {{ $t('SHOP.COMMON.MESSAGE.GOOGLE_RATING_NO_REVIEWS') }}
    </SfDataPlaceholder>
  </SfSection>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --google-review-slide-border: none;
    --google-review-slide-author-font-size: 0.875rem;
    --google-review-slide-text-font-size: 0.875rem;
    --google-review-slide-stars-size: 0.75rem;
    --google-review-stars-size: 1.375rem;
  }

  .google-review {
    --sf-stars-star-size: var(--google-review-slide-stars-size);

    text-align: center;

    .items-carousel {
      width: 80%;

      @include media.query(lg) {
        width: 70%;
      }

      @include media.query(sm) {
        width: 90%;
      }
    }
  }

  .reviews-carousel {
    display: flex;
    gap: 5rem;

    @include media.query(lg) {
      gap: 2rem;
    }

    @include media.query(sm) {
      flex-direction: column;
      align-items: center;
    }

    &__left {
      display: flex;
      flex-direction: column;
      justify-content: center;
      font-size: 0.875rem;
      font-weight: var(--font-weight-semi);
      width: 20%;
    }

    &__google-logo-wrapper {
      text-align: center;
      margin-top: 0.875rem;
    }

    &__average {
      --sf-stars-star-size: var(--google-review-stars-size);

      font-size: 1.5rem;
      text-align: center;
      font-weight: var(--font-weight-semi);

      .stars{
        justify-content: center;
        margin-top: 0.75rem;
      }
    }

    &__slide {
      height: 250px;
      background: white;
      padding: 1.5rem;
      border-radius: 20px;
      width: 100%;
      text-align: left;
      border: var(--google-review-slide-border)
    }

    &__author-name {
      font-weight: var(--font-weight-semi);
      font-size: var(--google-review-slide-author-font-size);
    }

    &__text {
      overflow-y: auto;
      max-height: 130px;
      padding-right: 0.2rem;
      font-size: var(--google-review-slide-text-font-size);

      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-thumb {
        background: var(--primary-border);
        border-radius: 4px;
      }
    }
  }
</style>
